import { makeStyles } from '@material-ui/styles'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { imagesVsSnapshotsDoc } from 'app/docLinks'
import DeleteImage from 'app/plugins/openstack/components/images/DeleteImage'
import { getVmSnapshotColumns, gridSearchFn } from 'app/plugins/openstack/components/images/helpers'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import ActionRequiredModalTrigger from 'core/components/ActionRequiredModalTrigger'
import CreateButton from 'core/components/buttons/CreateButton'
import DocumentMeta from 'core/components/DocumentMeta'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ListContainer from 'core/containers/ListContainer'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import { TopHeaderActionPortal } from 'core/elements/header/portals'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import Text from 'core/elements/Text'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { useLocalNetworkAccessAllowed } from 'core/hooks/useLocalNetworkAccessAllowed'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScrollPosition from 'core/hooks/useScrollPosition'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { isOpenCloud, isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import DataKeys from 'k8s/DataKeys'
import { listVolumeSnapshots } from 'openstack/components/storage/snapshots/actions'
import { volumeSnapshotsByIdSelector } from 'openstack/components/storage/snapshots/selectors'
import { listVirtualMachinesLite } from 'openstack/components/vms/actions'
import { virtualMachinesLiteByIdSelector } from 'openstack/components/vms/selectors'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import { humanReadableSize } from 'openstack/helpers'
import { pick, prop } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { durationBetweenDates } from 'utils/misc'
import { deleteImage, getHostGlanceEndpoint, listImages, testHostGlance } from './actions'
import AddImageModal from './AddImageModal'
import { checkImageLibraryRequiredActions } from './checkImageLibraryRequiredActions'
import DeleteVmSnapshot from './DeleteVmSnapshot'
import { ImageDetailsPageTabs } from './details/model'
import EditImageMetadataModal from './EditImageMetadataModal'
import EditImageModal from './EditImageModal'
import ImageStatusInfo from './ImageStatusInfo'
import ImportImageModal from './ImportImageModal'
import { ImagePageTabs } from './model'
import { imagesSelector } from './selectors'
import { octaviaHealthMonitorsSelector } from '../networks/selectors'
import { hslToRgb } from '@material-ui/core'

type ModelDataKey = DataKeys.Images
type SelectorModel = ArrayElement<ReturnType<typeof imagesSelector>>
type ActionParams = InferActionParams<typeof listImages>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>('Images', listTablePrefs)

export const TableHeaderTooltip = ({ message }) => {
  return <div>{message}</div>
}

const searchTargets = [
  'name',
  'id',
  'status',
  'visibility',
  'disk_format',
  'tags',
  'properties',
  'imageTypeDisplay',
]
const searchFunctions = [
  {
    property: 'properties',
    searchFn: (value, searchString) => gridSearchFn(value, searchString),
  },
]

export const TPMModelText = {
  'tpm-crb': 'CRB',
  'tpm-tis': 'TIS',
}

export const imageColumns = (imageListPage = true): GridViewColumn<SelectorModel>[] => [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id, name }) =>
        !!name
          ? routeManager.routes.openstack.imageDetails.path({
              id,
              detailsTab: ImageDetailsPageTabs.Overview,
            })
          : '',
    }),
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'status',
    label: <TitleWithInfoIcon titleText="Status" tooltipMsg={<ImageStatusInfo />} />,
  },
  {
    key: 'imageTypeDisplay',
    label: 'Type',
  },
  {
    key: 'visibility',
    label: 'Visibility',
  },
  {
    key: 'protected',
    label: 'Protected',
    render: (val) => (val ? 'True' : 'False'),
  },
  {
    key: 'disk_format',
    label: 'Disk Format',
  },
  {
    key: 'virtual_size',
    label: (
      <TitleWithInfoIcon
        titleText="Virtual Disk Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the VM disk that will get created from this image" />
        }
      />
    ),
    render: humanReadableSize,
  },
  {
    key: 'size',
    label: (
      <TitleWithInfoIcon
        titleText="File Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the snapshot file that is stored in the Image Library" />
        }
      />
    ),
    render: humanReadableSize,
  },
  {
    key: 'hw_tpm_model',
    label: 'Virtual TPM 2.0',
    render: (value, item) =>
      item?.hw_tpm_version === '2.0' ? `Enabled (${TPMModelText[value]})` : '',
  },
  {
    key: 'tags',
    label: 'Tags',
    render: (tags) => tags?.join(', '),
  },
  {
    key: 'properties',
    label: 'Properties',
    disableSorting: true,
    ...(imageListPage
      ? { CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }) }
      : {
          render: (value) => (
            <Labels
              labels={value}
              variant="default"
              separator={'='}
              containerType={'table'}
              maxVisible={1}
              showMoreButton={true}
            />
          ),
        }),
  },
  {
    key: 'created_at',
    label: 'Age',
    render: (value) => durationBetweenDates({ labels: ['d', 'h'] })(value),
  },
]

// const dropdownFilters = [
//   {
//     key: 'imageTypeDisplay',
//     label: 'Type',
//     FilterComponent: StringMultiDropdownFilter,
//     filterComponentProps: {
//       label: 'Type',
//     },
//     filterComponentOptionsPropName: 'dropdownOptions',
//     getOptionsFn: () => {
//       return [
//         {
//           label: 'VM Ephemeral Disk Snapshot',
//           value: 'VM Ephemeral Disk Snapshot',
//         },
//         {
//           label: 'VM Volume Snapshot',
//           value: 'VM Volume Snapshot',
//         },
//       ]
//     },
//     equalityComparerFn: (item, values) => {
//       return values?.includes(item?.imageTypeDisplay)
//     },
//   },
// ]

export default function ImagesListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { history, location, match } = useReactRouter()
  const { tab } = match.params

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const classes = useStyles()
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const scrollY = useScrollPosition()

  const offset = useMemo(() => {
    return { vertical: 8 + scrollY, horizontal: 0 }
  }, [scrollY])

  const [selectedImage, setSelectedImage] = useState<SelectorModel>(null)
  const [showEditImageModal, setShowEditImageModal] = useState<boolean>(false)
  const [showEditMetadataModal, setShowEditMetadataModal] = useState<boolean>(false)
  const [needToValidateCert, setNeedToValidateCert] = useState<boolean>(false)
  const [glanceHostNotReachable, setGlanceHostNotReachable] = useState<boolean>(false)
  const [hostEndpoint, setHostEndpoint] = useState('')
  const isBrowserLocalNetworkAccessAllowed = useLocalNetworkAccessAllowed()

  const { message, loading, reload, refreshing: imagesRefreshing } = useListAction(listImages, {
    params,
  })
  const data = useAppSelector(imagesSelector)

  const images = useMemo(() => {
    return data.filter((image) => image?.imageType === 'image')
  }, [data])
  const vmSnapshots = useMemo(() => {
    return data.filter((image) =>
      ['ephemeralDiskSnapshot', 'vmVolumeSnapshot'].includes(image?.imageType),
    )
  }, [data])

  const { loading: loadingVms } = useListAction(listVirtualMachinesLite)
  const vmsById = useSelector(virtualMachinesLiteByIdSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)

  const { loading: loadingHosts } = useListAction(listResmgrHosts)
  const hosts = useSelector(resmgrHostsSelector)
  const imageLibraryHosts = useMemo(() => {
    return hosts.filter((host) => !!host?.roles?.includes('image-library'))
  }, [hosts])

  const asyncCall = useCallback(async () => {
    try {
      const endpoint = await getHostGlanceEndpoint()
      setHostEndpoint(endpoint)
      await testHostGlance(endpoint)

      setGlanceHostNotReachable(false)
      setNeedToValidateCert(false)
    } catch (err) {
      if (err?.err?.message === 'Network Error') {
        setNeedToValidateCert(true)
      } else if (err?.err?.code === 'ECONNABORTED') {
        setGlanceHostNotReachable(true)
      }
    }
  }, [getHostGlanceEndpoint, testHostGlance])

  useEffect(() => {
    asyncCall()
  }, [asyncCall])

  const vmSnapshotColumns: GridViewColumn<SelectorModel>[] = getVmSnapshotColumns({
    vmsById,
    volumeSnapshotsById,
  })

  const actionRequiredIssues = useMemo(
    () =>
      checkImageLibraryRequiredActions({
        needToValidateCert,
        isBrowserLocalNetworkAccessAllowed,
        glanceHostNotReachable,
        hostEndpoint,
      }),
    [needToValidateCert, hostEndpoint, isBrowserLocalNetworkAccessAllowed, glanceHostNotReachable],
  )

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      label: 'Edit',
      icon: 'edit',
      cond: (selectedItems) => selectedItems.length === 1,
      disabledTooltip: 'You can only edit one image at a time',
      handleAction: ([image]) => {
        setSelectedImage(image)
        setShowEditImageModal(true)
      },
      refreshAfterSuccess: true,
    },
    {
      cond: (selectedItems) => selectedItems.length === 1 && selectedItems.every((item) => !!item),
      disabledTooltip: 'You can only edit properties for a single image at a time',
      label: 'Edit Properties',
      icon: 'tags',
      handleAction: ([image]) => {
        setSelectedImage(image)
        setShowEditMetadataModal(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  const hideImageUploadButton = isOpenCloud()

  return (
    <>
      <DocumentMeta title="Images" />
      <PollingData
        hidden
        loading={loading}
        onReload={() => {
          return new Promise<void>((resolve) => {
            reload(true, false)
            asyncCall()
            resolve()
          })
        }}
        refreshDuration={1000 * 30}
      />
      {/* <TopHeaderActionPortal>
        {actionRequiredIssues.length ? (
          <ActionRequiredModalTrigger
            issues={actionRequiredIssues}
            modalTitle="Action Required"
            modalIntroText="The following actions are required to access the Image Library Endpoint:"
            trigger={({ openWithEvent }) => (
              <Text variant="subtitle2" className={classes.warning} onClick={openWithEvent}>
                <FontAwesomeIcon className={classes.icon}>info-circle</FontAwesomeIcon>
                {glanceHostNotReachable
                  ? 'Image Library Host Not Reachable - Action Required'
                  : 'Action Required'}{' '}
                ({actionRequiredIssues.length})
              </Text>
            )}
          />
        ) : null}
      </TopHeaderActionPortal> */}
      <ImportImageModal addRoute={routeManager.routes.openstack.importImage} />
      {!isVmware && (
        <AddImageModal
          addRoute={routeManager.routes.openstack.addImage}
          actionRequiredIssues={actionRequiredIssues}
          glanceHostNotReachable={glanceHostNotReachable}
          imageLibraryPresent={!!imageLibraryHosts?.length}
        />
      )}
      {showEditImageModal && (
        <EditImageModal onClose={() => setShowEditImageModal(false)} rows={[selectedImage]} />
      )}
      {showEditMetadataModal && (
        <EditImageMetadataModal
          onClose={() => setShowEditMetadataModal(false)}
          rows={[selectedImage]}
        />
      )}
      <Tabs route={routeManager.routes.openstack.images}>
        <Tab label="Images" value={ImagePageTabs.Images}>
          <ListContainer<ModelDataKey, SelectorModel>
            dataKey={DataKeys.Images}
            searchTargets={searchTargets}
            searchFunctions={searchFunctions}
            uniqueIdentifier="id"
            loading={loading}
            loadingMessage={message}
            onRefresh={() => {
              reload(true)
              asyncCall()
            }}
            refreshing={imagesRefreshing}
            data={images}
            columns={imageColumns(true)}
            addUrl={
              hideImageUploadButton || isVmware
                ? null
                : routeManager.routes.openstack.addImage.path({ tab })
            }
            addText={hideImageUploadButton || isVmware ? null : 'Add Image'}
            getParamsUpdater={getParamsUpdater}
            batchActions={batchActions}
            deleteAction={deleteImage}
            deleteCond={(selectedItems) => !selectedItems?.some((item) => item?.protected)}
            deleteDisabledInfo="Unable to delete protected images"
            DeleteDialogComponent={DeleteImage}
            multiSelection
            extraHeaderContent={
              hideImageUploadButton || isReadonlyUser() ? null : (
                <CreateButton
                  variant="secondary"
                  onClick={() =>
                    history.push(routeManager.routes.openstack.importImage.path({ tab }))
                  }
                >
                  Import With CLI
                </CreateButton>
              )
            }
            {...pick(listTablePrefs, params)}
          />
        </Tab>
        <Tab label="VM Snapshots" value={ImagePageTabs.Snapshots}>
          <ListContainer<ModelDataKey, SelectorModel>
            dataKey={DataKeys.Images}
            label="VM Snapshots"
            searchTargets={searchTargets}
            searchFunctions={searchFunctions}
            uniqueIdentifier="id"
            loading={loading}
            loadingMessage={message}
            onRefresh={() => {
              reload(true)
              asyncCall()
            }}
            refreshing={imagesRefreshing}
            data={vmSnapshots}
            columns={vmSnapshotColumns}
            addUrl={
              hideImageUploadButton || isVmware
                ? null
                : routeManager.routes.openstack.addImage.path({ tab })
            }
            addText={hideImageUploadButton || isVmware ? null : 'Add Image'}
            getParamsUpdater={getParamsUpdater}
            batchActions={batchActions}
            deleteAction={deleteImage}
            deleteCond={(selectedItems) => !selectedItems?.some((item) => item?.protected)}
            deleteDisabledInfo="Unable to delete protected images"
            DeleteDialogComponent={DeleteVmSnapshot}
            multiSelection
            // dropdownFilters={dropdownFilters}
            extraHeaderContent={
              <>
                <ExternalLink url={imagesVsSnapshotsDoc}>Read About Image Types</ExternalLink>
                {hideImageUploadButton || isReadonlyUser() ? null : (
                  <CreateButton
                    variant="secondary"
                    onClick={() =>
                      history.push(routeManager.routes.openstack.importImage.path({ tab }))
                    }
                  >
                    Import With CLI
                  </CreateButton>
                )}
              </>
            }
            {...pick(listTablePrefs, params)}
          />
        </Tab>
      </Tabs>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  icon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
  warning: {
    color: theme.components.badge.error.color,
    cursor: 'pointer',

    '& > span': {
      color: theme.components.badge.error.color,
    },
  },
}))
